/**
 * AppShell — v6 layout with NavBar + Outlet
 *
 * Desktop: top nav bar
 * Mobile: bottom tab bar
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider } from "@/components/AuthProvider";
import { useAuthStore } from "@/store/auth";
import { NotificationBell, NotificationPanel } from "@/components/NotificationPanel";
import { useNotifications } from "@/hooks/use-notifications";
import { ThemeToggle } from "@/components/ThemeToggle";
import { OfflineBanner } from "@/components/OfflineBanner";
// #725: DataModeIndicator removed from global render — use ?debug=1 URL param instead
import { SearchOverlay } from "@/components/SearchOverlay";
import { Avatar } from "@/components/Avatar";
import { PrefetchLink } from "@/components/PrefetchLink";
import { warmCoreData } from "@/lib/prefetch";
import { onWriteFailure } from "@/lib/write-through-queue";
import { useToastStore } from "@/store/toast";
import { useTopics } from "@/hooks/use-supabase";

/* ── Tab definitions ── */
interface TabDef {
  path: string;
  label: string;
  icon: string;
  match: (p: string) => boolean;
  /** #F9: Hide from new users until they've had enough sessions */
  progressive?: boolean;
}

const allTabs: TabDef[] = [
  { path: "/topics", label: "推荐", icon: "topic", match: (p) => p === "/" || p === "/topics" || p.startsWith("/topic/") },
  { path: "/follow", label: "关注", icon: "follow", match: (p) => p === "/follow" },
  // #724: Club tab now live — no longer progressive-gated
  { path: "/club", label: "Club", icon: "club", match: (p) => p.startsWith("/club") },
  { path: "/ai-talk", label: "AI 对谈", icon: "ai", match: (p) => p.startsWith("/ai-talk") },
];

/** #F9: Progressive disclosure — show all tabs after 5 sessions */
function useVisibleTabs(): TabDef[] {
  // Count visits to decide if user is ready for advanced features
  let isExperienced = false;
  try {
    const visits = parseInt(localStorage.getItem("ig-visit-count") ?? "0", 10);
    localStorage.setItem("ig-visit-count", String(visits + 1));
    isExperienced = visits >= 5;
  } catch { /* noop */ }
  return isExperienced ? allTabs : allTabs.filter(t => !t.progressive);
}

// #390: Mobile-only "我的" tab — covers profile/messages/bookmarks/activity/settings
const mobileOnlyTab: TabDef = {
  path: "/profile",
  label: "我的",
  icon: "me",
  match: (p) => p.startsWith("/profile") || p.startsWith("/messages") || p.startsWith("/bookmarks") || p.startsWith("/activity") || p.startsWith("/export") || p.startsWith("/weekly-review"),
};
// @ts-expect-error mobileTabs kept for future mobile progressive disclosure
const _mobileTabs: TabDef[] = [...allTabs, mobileOnlyTab];

export function AppShell() {
  const { pathname } = useLocation();
  const user = useAuthStore((s) => s.user);
  const [notifOpen, setNotifOpen] = useState(false);
  const { notifications: realNotifs, unreadCount: realUnread, markAllRead: markAllRealRead } = useNotifications(user?.id ?? null);
  const [searchOpen, setSearchOpen] = useState(false);
  const tabs = useVisibleTabs();
  // Mobile tabs always include all + "我的"
  const visibleMobileTabs = [...tabs, mobileOnlyTab];

  // Warm core data on first mount (fire once)
  const warmedRef = useRef(false);
  const addToast = useToastStore((s) => s.addToast);
  useEffect(() => {
    if (!warmedRef.current) {
      warmedRef.current = true;
      warmCoreData();
      // #686: Show toast on write-through failure
      onWriteFailure((_table, _op, label) => {
        addToast(`操作保存失败，将自动重试${label ? `（${label}）` : ''}`, 'warning');
      });
    }
    return () => { onWriteFailure(null); };
  }, [addToast]);

  return (
    <AuthProvider>
    <div className="min-h-screen flex flex-col">
      {/* ── Desktop Top Nav ── */}
      <header className="hidden md:flex items-center h-14 px-6 border-b border-[var(--ig-border)] bg-[var(--ig-surface)]/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-6xl mx-auto w-full flex items-center">
          <Link to="/" className="font-bold text-xl text-[var(--ig-accent)] mr-6 no-underline shrink-0 flex items-center gap-2">
            <svg viewBox="60 50 360 430" className="w-7 h-7 shrink-0"><rect x="80" y="72" width="232" height="232" rx="12" fill="#3b82f6" opacity=".55"/><circle cx="332" cy="212" r="128" fill="#8b5cf6" opacity=".55"/><polygon points="256,200 396,308 342,452 170,452 116,308" fill="#06b6d4" opacity=".55"/></svg>
            萃思
          </Link>
          <nav className="flex gap-1" aria-label="主导航">
            {tabs.map((t) => {
              const active = t.match(pathname);
              return (
                <PrefetchLink
                  key={t.path}
                  to={t.path}
                  aria-current={active ? "page" : undefined}
                  className={`px-5 py-3 rounded-lg text-sm font-medium transition-colors no-underline flex items-center gap-2 ${
                    active
                      ? "bg-[var(--ig-accent-soft)] text-[var(--ig-accent)]"
                      : "text-[var(--ig-text-2)] hover:bg-[var(--ig-surface-alt)]"
                  }`}
                >
                  <TabIcon name={t.icon} active={active} />
                  {t.label}
                </PrefetchLink>
              );
            })}
          </nav>
          {/* Search bar with hot topic carousel */}
          <NavSearchBar onClick={() => setSearchOpen(true)} />

          <div className="flex items-center gap-3 shrink-0">
            <ThemeToggle />

            {/* Divider (#429) */}
            <span className="w-px h-5 bg-slate-200 dark:bg-slate-700" />

            {/* Group 2: Personal — unified flex container for consistent icon gap (#587) */}
            {user && (
              <div className="flex items-center gap-3" data-testid="personal-icons">
                <div className="relative">
                  <NotificationBell
                    onClick={() => setNotifOpen((v) => !v)}
                    unreadCount={realUnread}
                  />
                  <NotificationPanel
                    open={notifOpen}
                    onClose={() => setNotifOpen(false)}
                    realNotifications={realNotifs}
                    onMarkAllRead={markAllRealRead}
                  />
                </div>
                <Link
                  to="/bookmarks"
                  className="text-[var(--ig-text-2)] hover:text-[var(--ig-accent)] no-underline flex items-center"
                  aria-label="收藏夹"
                  title="收藏"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
                  </svg>
                </Link>
                {/* #724: Messages icon removed — no real messaging backend yet */}
              </div>
            )}
            {user ? (
              <AvatarDropdown user={user} />
            ) : (
              <Link
                to="/login"
                className="px-4 py-2 rounded-lg bg-[var(--ig-accent)] text-white text-sm font-medium no-underline hover:bg-[var(--ig-accent-hover)] transition"
              >
                登录
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* ── Offline Banner ── */}
      <OfflineBanner />

      {/* #725: DataModeIndicator removed — was showing dev label in production */}

      {/* ── Main Content ── */}
      <main className="flex-1 pb-20 md:pb-0">
        <Outlet />
      </main>

      {/* ── Mobile Bottom Tabs (#390: added "我的" 5th tab) ── */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 h-16 bg-[var(--ig-surface)] border-t border-[var(--ig-border)] flex z-50 safe-area-pb" aria-label="移动端导航">
        {visibleMobileTabs.map((t) => {
          const active = t.match(pathname);
          return (
            <PrefetchLink
              key={t.path}
              to={t.path}
              aria-current={active ? "page" : undefined}
              aria-label={t.label}
              className={`flex-1 flex flex-col items-center justify-center gap-1 text-xs no-underline relative ${
                active ? "text-[var(--ig-accent)] font-medium" : "text-[var(--ig-text-3)]"
              }`}
            >
              {active && <span className="absolute top-0 left-1/4 right-1/4 h-0.5 rounded-full bg-[var(--ig-accent)]" />}
              <MobileTabIcon name={t.icon} active={active} />
              {t.label}
            </PrefetchLink>
          );
        })}
      </nav>

      {/* ── Global Search Overlay (#389, #282: lazy mount to avoid fetch-on-load) ── */}
      {searchOpen && <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />}
    </div>
    </AuthProvider>
  );
}

/* ── Tab SVG icons (#265: unified desktop/mobile) ── */
const TAB_PATHS: Record<string, string> = {
  follow: "M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z",
  topic: "M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18",
  club: "M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155",
  ai: "M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z",
  // #390: "我的" person icon for mobile bottom tab
  me: "M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z",
};

function NavIcon({ name, active, size = "w-4.5 h-4.5", activeColor = "text-[var(--ig-accent)]", inactiveColor = "text-[var(--ig-text-2)]" }: {
  name: string; active: boolean; size?: string; activeColor?: string; inactiveColor?: string;
}) {
  const d = TAB_PATHS[name];
  if (!d) return null;
  return (
    <svg className={`${size} ${active ? activeColor : inactiveColor}`} fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

function TabIcon({ name, active }: { name: string; active: boolean }) {
  return <NavIcon name={name} active={active} />;
}

function MobileTabIcon({ name, active }: { name: string; active: boolean }) {
  return <NavIcon name={name} active={active} size="w-6 h-6" inactiveColor="text-[var(--ig-text-3)]" />;
}

/* ── AvatarDropdown — user menu with profile link + logout ── */
function AvatarDropdown({ user }: { user: { id: string; display_name?: string | null; avatar_url?: string | null } }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const signOut = useAuthStore((s) => s.signOut);

  // Close on outside click
  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
  }, []);
  useEffect(() => {
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, handleClickOutside]);

  const handleLogout = async () => {
    setOpen(false);
    await signOut();
    navigate("/welcome");
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className="rounded-full focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
        aria-label="用户菜单"
        aria-expanded={open}
      >
        <Avatar userId={user.id} name={user.display_name} avatarUrl={user.avatar_url} size="md" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-48 bg-[var(--ig-surface)] rounded-xl border border-slate-200 dark:border-slate-700 shadow-lg py-1 z-[var(--z-dropdown)] animate-in fade-in slide-in-from-top-1 duration-150">
          <Link
            to={`/profile/${user.id}`}
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 no-underline transition"
          >
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>
            我的主页
          </Link>
          <Link
            to="/bookmarks"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 no-underline transition"
          >
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" /></svg>
            我的收藏
          </Link>
          <Link
            to="/activity"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 no-underline transition"
          >
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            浏览记录
          </Link>
          <Link
            to="/settings"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 no-underline transition"
          >
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.248a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.28z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            设置
          </Link>
          <div className="mx-3 my-1 border-t border-slate-100 dark:border-slate-700" />
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 w-full text-left transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
            退出登录
          </button>
        </div>
      )}
    </div>
  );
}

/* ── NavSearchBar — long search bar with rotating hot topic placeholder ── */

/** Fallback placeholder titles when topics haven't loaded yet */
const FALLBACK_PLACEHOLDERS = [
  "搜索话题、对谈、讨论...",
  "今日热点：AI 与创造力",
  "大家在聊：远程协作的信任",
];

function NavSearchBar({ onClick }: { onClick: () => void }) {
  const { data } = useTopics({ limit: 10 });
  const topics = data?.topics ?? [];

  // Build carousel items from hot topics
  const placeholders = topics.length > 0
    ? topics
        .sort((a, b) => b.cluster_density - a.cluster_density)
        .slice(0, 6)
        .map(t => t.title)
    : FALLBACK_PLACEHOLDERS;

  const [index, setIndex] = useState(0);
  const [fading, setFading] = useState(false);

  // Rotate placeholder every 3.5s with fade transition
  useEffect(() => {
    if (placeholders.length <= 1) return;
    let fadeTimer: ReturnType<typeof setTimeout> | null = null;
    const interval = setInterval(() => {
      setFading(true);
      fadeTimer = setTimeout(() => {
        setIndex(i => (i + 1) % placeholders.length);
        setFading(false);
      }, 300);
    }, 3500);
    return () => { clearInterval(interval); if (fadeTimer) clearTimeout(fadeTimer); };
  }, [placeholders.length]);

  return (
    <button
      onClick={onClick}
      className="ml-4 flex-1 max-w-md flex items-center gap-2.5 h-9 px-3.5 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 hover:border-blue-300 hover:bg-white dark:hover:bg-slate-800 transition-all cursor-pointer group"
      aria-label="搜索"
    >
      <svg className="w-4 h-4 text-slate-400 group-hover:text-blue-500 transition shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
      </svg>
      <span
        className={`text-sm text-slate-400 truncate transition-opacity duration-300 ${fading ? "opacity-0" : "opacity-100"}`}
      >
        {placeholders[index % placeholders.length]}
      </span>
    </button>
  );
}